import { X, Settings } from "lucide-react";
import { XFAFormWizardPreviewNew } from "../components/XFAFormWizardPreviewNew";
import { Template } from "../api/endpoints/fetchTemplates";
import { useXFAFormTemplateDetails } from "../hooks/useXFAFormTemplateDetails";
import { SkeletonPreview } from "../components/SkeletonPreview";

interface XFAFormPreviewProps {
  template: Template;
  onClose?: () => void;
  onInfo?: () => void;
  isInfoPanelOpen?: boolean;
  hideCloseButton?: boolean;
  onSaved?: (fileId: string) => void;
}

export function XFAFormPreview({
  template,
  onClose,
  onInfo,
  isInfoPanelOpen,
  hideCloseButton = false,
  onSaved,
}: XFAFormPreviewProps) {
  const displayTitle = template.fileTitle || template.fileName;

  // implement if user is offline then get the data from indexedDB 

  const { data, isLoading, isError, error } = useXFAFormTemplateDetails({
    fileId: template.fileId,
    userEmail: template.userEmail,
  });
  const templateDetails = data?.data;

  return (
    <div
      className={`preview-form fixed inset-0 bg-white z-50 flex flex-col transition-all duration-300 ${isInfoPanelOpen ? "backdrop-blur-sm bg-white/95" : ""
        }`}
    >
      <div
        id="preview-header"
        className={`bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between transition-all duration-300 ${isInfoPanelOpen ? "bg-white/90 backdrop-blur-sm" : ""
          }`}
      >
        <div>
          <h1 className="text-lg font-normal text-gray-900">
            Preview: {displayTitle || "Untitled Template"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {onInfo && (
            <button
              onClick={onInfo}
              className="text-xs px-3 py-1.5 btn-primary"
            >
              <Settings className="w-3 h-3" />
              Form Info
            </button>
          )}
          {!hideCloseButton && (
            <button onClick={onClose} className="btn btn-transparent">
              <X className="w-6 h-6" />
            </button>
          )}
        </div>
      </div>

      <div
        className={`flex-1 overflow-hidden transition-all duration-300 ${isInfoPanelOpen ? "opacity-75" : ""
          }`}
      >
        {isLoading && <SkeletonPreview />}

        {isError && (
          <div className="h-full flex flex-col items-center justify-center gap-2 text-center text-sm text-red-500">
            Unable to load the template preview.
            {error instanceof Error && (
              <span className="text-xs text-gray-400">{error.message}</span>
            )}
          </div>
        )}
        {!isLoading && !isError && templateDetails && (
          <XFAFormWizardPreviewNew
            template={templateDetails}
            templateId={template.id}
            htmlId={template.htmlId}
            onClose={onClose}
            memberId={template?.memberId}
            onSaveSuccess={(fileId) => {
              if (onSaved) onSaved(fileId);
            }}
          />
        )}

        {!isLoading && !isError && !templateDetails && (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">
            Template preview is not available.
          </div>
        )}
      </div>
    </div>
  );
}

export default XFAFormPreview;
